import { router, useForm, usePage } from '@inertiajs/react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { ErrorSummary } from '@/components/public/forms/ErrorSummary';
import FormActions from '@/components/public/forms/FormActions';
import { SubmissionReceipt } from '@/components/public/forms/SubmissionReceipt';
import DatePicker from '@/components/public/ui/DatePicker';
import Select from '@/components/public/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';
import { buildErrorSummaryItems } from '@/lib/errorSummary';
import { inputClass, inputErrorClass, sectionLabel } from '@/lib/formStyles';
import {
  birthDateError,
  todayISODate,
  validateEmail,
  validatePhonePT,
  validatePostalCode,
} from '@/lib/validators';

type FlashProps = { flash: { notice?: string; alert?: string } };

type Payload = {
  name: string;
  birthDate: string;
  documentType: string;
  documentNumber: string;
  documentTypeOther: string;
  fatherName: string;
  motherName: string;
  maritalStatus: string;
  phone: string;
  email: string;
  street: string;
  postalCode: string;
  locality: string;
  residenceTime: string;
  purpose: string;
  purposeOther: string;
  pickupLocation: string;
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome completo',
  birthDate: 'Data de nascimento',
  documentType: 'Tipo de documento',
  documentNumber: 'N.º do documento',
  documentTypeOther: 'Tipo de documento',
  fatherName: 'Nome do pai',
  motherName: 'Nome da mãe',
  maritalStatus: 'Estado civil',
  phone: 'Telefone',
  email: 'Correio eletrónico',
  street: 'Morada',
  postalCode: 'Código postal',
  locality: 'Localidade',
  residenceTime: 'Tempo de residência',
  purpose: 'Finalidade',
  purposeOther: 'Finalidade',
  pickupLocation: 'Local de levantamento',
  consent: 'Consentimento',
  files: 'Anexos',
  type: 'Tipo de pedido',
};

const FIELD_ANCHORS = new Set([
  'name',
  'birthDate',
  'documentType',
  'documentNumber',
  'documentTypeOther',
  'fatherName',
  'motherName',
  'maritalStatus',
  'phone',
  'email',
  'street',
  'postalCode',
  'locality',
  'residenceTime',
  'purpose',
  'purposeOther',
  'pickupLocation',
]);

export default function ResidenceCertificateForm() {
  const { flash, pickupLocations } = usePage<
    FlashProps & { pickupLocations: { value: string; label: string }[] }
  >().props;
  const idPrefix = useId();
  const fid = (name: string) => `${idPrefix}-${name}`;
  const errId = (name: string) => `${idPrefix}-${name}-error`;

  const form = useForm<{ payload: Payload; files: File[]; consent: boolean; consented_at: string }>(
    {
      payload: {
        name: '',
        birthDate: '',
        documentType: '',
        documentNumber: '',
        documentTypeOther: '',
        fatherName: '',
        motherName: '',
        maritalStatus: '',
        phone: '',
        email: '',
        street: '',
        postalCode: '',
        locality: '',
        residenceTime: '',
        purpose: '',
        purposeOther: '',
        pickupLocation: '',
      },
      files: [],
      consent: false,
      consented_at: '',
    },
  );

  const { data, setData, processing, errors: rawErrors, reset } = form;
  const errors = rawErrors as unknown as Record<string, string | undefined>;

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  const set =
    (field: keyof Payload) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setData('payload', { ...data.payload, [field]: e.target.value });

  const validateField = (field: string, value: string): string | null => {
    if (!value) return null;
    if (field === 'phone' && !validatePhonePT(value)) return 'Número de telefone inválido.';
    if (field === 'postalCode' && !validatePostalCode(value))
      return 'Código postal inválido. Use o formato NNNN-NNN.';
    if (field === 'email' && !validateEmail(value)) return 'Correio eletrónico inválido.';
    if (field === 'birthDate') return birthDateError(value);
    return null;
  };

  const handleBlur = (field: keyof Payload) => () => {
    const value = String(data.payload[field]);
    const err = validateField(field, value);
    setClientErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const phoneErr = validateField('phone', data.payload.phone);
    if (phoneErr) errs.phone = phoneErr;
    const postalErr = validateField('postalCode', data.payload.postalCode);
    if (postalErr) errs.postalCode = postalErr;
    const emailErr = validateField('email', data.payload.email);
    if (emailErr) errs.email = emailErr;
    const birthErr = validateField('birthDate', data.payload.birthDate);
    if (birthErr) errs.birthDate = birthErr;
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'residence_certificate',
        error_count: Object.keys(errs).length,
      });
      return;
    }
    form.transform((d) => ({
      type: 'residence_certificate',
      consent: 'true',
      consented_at: new Date().toISOString(),
      payload: d.payload,
      files: d.files,
    }));
    trackUmamiEvent('service_request_submit', {
      service_type: 'residence_certificate',
      has_files: data.files.length > 0,
    });
    form.post('/service-requests', { forceFormData: true });
  };

  const fieldErr = (field: string) => clientErrors[field] || errors[field];
  const errorItems = buildErrorSummaryItems({ ...errors, ...clientErrors }, FIELD_LABELS, (key) =>
    FIELD_ANCHORS.has(key) ? `#${fid(key)}` : undefined,
  );

  const p = data.payload;
  const isFormValid =
    !!p.name &&
    !!p.birthDate &&
    !!p.documentType &&
    !!p.documentNumber &&
    (p.documentType !== 'outro' || !!p.documentTypeOther.trim()) &&
    !!p.fatherName &&
    !!p.motherName &&
    !!p.maritalStatus &&
    !!p.phone &&
    !!p.email &&
    !!p.street &&
    !!p.postalCode &&
    !!p.locality &&
    !!p.residenceTime &&
    !!p.purpose &&
    (p.purpose !== 'outro' || !!p.purposeOther.trim()) &&
    !!p.pickupLocation &&
    data.consent &&
    Object.keys(clientErrors).length === 0;

  if (flash?.notice) {
    return (
      <SubmissionReceipt
        formType="residence_certificate"
        title="Pedido submetido à Junta"
        message={flash.notice}
        steps={[
          { label: 'Registo', description: 'O pedido ficou guardado com data e consentimento.' },
          {
            label: 'Análise',
            description: 'Os serviços confirmam os dados e documentos indicados.',
          },
          {
            label: 'Contacto',
            description: 'Os serviços entrarão em contacto se forem necessários esclarecimentos.',
          },
        ]}
        actions={[
          {
            label: 'Submeter outro pedido',
            onClick: () => {
              reset();
              setClientErrors({});
              router.get(window.location.pathname, {}, { replace: true, preserveScroll: true });
            },
          },
          { label: 'Voltar aos serviços', href: '/services', variant: 'primary' },
        ]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-8">
        <ErrorSummary items={errorItems} />

        {/* Dados Pessoais */}
        <div>
          <span className={sectionLabel('primary')}>Dados Pessoais</span>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('name')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nome completo
              </label>
              <input
                id={fid('name')}
                className={inputClass}
                value={p.name}
                onChange={set('name')}
                placeholder="Ex: Maria da Silva Santos"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('birthDate')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Data de Nascimento
              </label>
              <DatePicker
                id={fid('birthDate')}
                value={p.birthDate}
                onChange={(value) => {
                  setData('payload', { ...p, birthDate: value });
                  const err = validateField('birthDate', value);
                  setClientErrors((prev) => {
                    const next = { ...prev };
                    if (err) next.birthDate = err;
                    else delete next.birthDate;
                    return next;
                  });
                }}
                max={todayISODate()}
                hasError={!!fieldErr('birthDate')}
                aria-invalid={!!fieldErr('birthDate')}
                aria-describedby={fieldErr('birthDate') ? errId('birthDate') : undefined}
                required
              />
              {fieldErr('birthDate') && (
                <p id={errId('birthDate')} className="text-error mt-1 text-xs">
                  {fieldErr('birthDate')}
                </p>
              )}
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('documentType')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Tipo de Documento
              </label>
              <Select
                id={fid('documentType')}
                value={p.documentType}
                onChange={(e) => {
                  const next = e.target.value;
                  setData('payload', {
                    ...p,
                    documentType: next,
                    documentTypeOther: next === 'outro' ? p.documentTypeOther : '',
                  });
                }}
                required
              >
                <option value="">Selecionar</option>
                <option value="cc">Cartão de Cidadão</option>
                <option value="passaporte">Passaporte</option>
                <option value="residencia">Título de Residência</option>
                <option value="outro">Outro</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('documentNumber')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                N.º do documento
              </label>
              <input
                id={fid('documentNumber')}
                className={inputClass}
                value={p.documentNumber}
                onChange={set('documentNumber')}
                placeholder="Ex: 12345678 9 ZZ4"
                required
              />
            </div>
          </div>
          {p.documentType === 'outro' && (
            <div className="mb-4">
              <label
                htmlFor={fid('documentTypeOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique o tipo de documento
              </label>
              <input
                id={fid('documentTypeOther')}
                className={inputClass}
                value={p.documentTypeOther}
                onChange={set('documentTypeOther')}
                placeholder="Ex: Carta de Condução, Passaporte estrangeiro…"
                required
              />
            </div>
          )}
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('maritalStatus')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Estado Civil
              </label>
              <Select
                id={fid('maritalStatus')}
                value={p.maritalStatus}
                onChange={set('maritalStatus')}
                required
              >
                <option value="">Selecionar</option>
                <option value="solteiro">Solteiro(a)</option>
                <option value="casado">Casado(a)</option>
                <option value="uniao-de-facto">União de Facto</option>
                <option value="divorciado">Divorciado(a)</option>
                <option value="separado">Separado(a)</option>
                <option value="viuvo">Viúvo(a)</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('phone')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Contacto Telefónico
              </label>
              <input
                id={fid('phone')}
                type="tel"
                className={fieldErr('phone') ? inputErrorClass : inputClass}
                value={p.phone}
                onChange={set('phone')}
                onBlur={handleBlur('phone')}
                aria-invalid={!!fieldErr('phone')}
                aria-describedby={fieldErr('phone') ? errId('phone') : undefined}
                placeholder="+351 9XX XXX XXX"
                required
              />
              {fieldErr('phone') && (
                <p id={errId('phone')} className="text-error mt-1 text-xs">
                  {fieldErr('phone')}
                </p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor={fid('email')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Correio eletrónico
            </label>
            <input
              id={fid('email')}
              type="email"
              className={fieldErr('email') ? inputErrorClass : inputClass}
              value={p.email}
              onChange={set('email')}
              onBlur={handleBlur('email')}
              aria-invalid={!!fieldErr('email')}
              aria-describedby={fieldErr('email') ? errId('email') : undefined}
              placeholder="email@exemplo.pt"
              required
            />
            {fieldErr('email') && (
              <p id={errId('email')} className="text-error mt-1 text-xs">
                {fieldErr('email')}
              </p>
            )}
          </div>
        </div>

        {/* Filiação */}
        <div>
          <span className={sectionLabel('primary')}>Filiação</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('fatherName')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nome do pai
              </label>
              <input
                id={fid('fatherName')}
                className={inputClass}
                value={p.fatherName}
                onChange={set('fatherName')}
                placeholder="Ex: António da Silva Santos"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('motherName')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nome da mãe
              </label>
              <input
                id={fid('motherName')}
                className={inputClass}
                value={p.motherName}
                onChange={set('motherName')}
                placeholder="Ex: Maria da Silva Santos"
                required
              />
            </div>
          </div>
        </div>

        {/* Morada */}
        <div>
          <span className={sectionLabel('primary')}>Morada de Residência</span>
          <div className="mb-4">
            <label
              htmlFor={fid('street')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Lugar
            </label>
            <input
              id={fid('street')}
              className={inputClass}
              value={p.street}
              onChange={set('street')}
              placeholder="Ex: Rua da Igreja, n.º 12"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label
                htmlFor={fid('postalCode')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Código postal
              </label>
              <input
                id={fid('postalCode')}
                className={fieldErr('postalCode') ? inputErrorClass : inputClass}
                value={p.postalCode}
                onChange={set('postalCode')}
                onBlur={handleBlur('postalCode')}
                pattern="\d{4}-\d{3}"
                maxLength={8}
                aria-invalid={!!fieldErr('postalCode')}
                aria-describedby={fieldErr('postalCode') ? errId('postalCode') : undefined}
                placeholder="4705-000"
                required
              />
              {fieldErr('postalCode') && (
                <p id={errId('postalCode')} className="text-error mt-1 text-xs">
                  {fieldErr('postalCode')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('locality')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Localidade
              </label>
              <input
                id={fid('locality')}
                className={inputClass}
                value={p.locality}
                onChange={set('locality')}
                placeholder="Localidade"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('residenceTime')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Tempo de residência
              </label>
              <Select
                id={fid('residenceTime')}
                value={p.residenceTime}
                onChange={set('residenceTime')}
                required
              >
                <option value="">Selecionar</option>
                <option value="lt1">Menos de 1 ano</option>
                <option value="1-5">1 a 5 anos</option>
                <option value="5-10">5 a 10 anos</option>
                <option value="gt10">Mais de 10 anos</option>
              </Select>
            </div>
          </div>
        </div>

        {/* Pedido */}
        <div>
          <span className={sectionLabel('primary')}>Pedido</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('purpose')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Finalidade
              </label>
              <Select
                id={fid('purpose')}
                value={p.purpose}
                onChange={(e) => {
                  const next = e.target.value;
                  setData('payload', {
                    ...p,
                    purpose: next,
                    purposeOther: next === 'outro' ? p.purposeOther : '',
                  });
                }}
                required
              >
                <option value="">Selecionar</option>
                <option value="banco">Banco / Crédito</option>
                <option value="escola">Escola / Universidade</option>
                <option value="trabalho">Trabalho</option>
                <option value="seg-social">Segurança Social</option>
                <option value="imi">Isenção IMI</option>
                <option value="outro">Outro</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('pickupLocation')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Local de levantamento
              </label>
              <Select
                id={fid('pickupLocation')}
                value={p.pickupLocation}
                onChange={set('pickupLocation')}
                required
              >
                <option value="">Selecionar</option>
                {pickupLocations.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {p.purpose === 'outro' && (
            <div className="mt-4">
              <label
                htmlFor={fid('purposeOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique a finalidade
              </label>
              <input
                id={fid('purposeOther')}
                className={inputClass}
                value={p.purposeOther}
                onChange={set('purposeOther')}
                placeholder="Ex: Processo de cidadania, candidatura a apoio…"
                required
              />
            </div>
          )}
        </div>

        {/* Consent */}
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={data.consent}
            onChange={(e) => setData('consent', e.target.checked)}
            className="mt-1 rounded"
            required
          />
          <span className="text-on-surface-light text-sm leading-relaxed">
            Declaro que os dados fornecidos são verdadeiros e tomei conhecimento do tratamento dos
            meus dados pessoais para efeitos deste pedido, nos termos da política de privacidade.
          </span>
        </label>

        <FormActions
          submitLabel="Submeter pedido"
          submittingLabel="A submeter…"
          submittingHint="A enviar os dados de forma segura"
          processing={processing}
          disabled={!isFormValid}
          onReset={() => {
            reset();
            setClientErrors({});
          }}
        />
      </form>
    </div>
  );
}
